import React, { createContext, useCallback, useContext, useState } from "react";
import ErrorDialog from "../components/common/ErrorDialog";
import ConfirmDialog from "../components/common/ConfirmDialog";

export type ShowErrorOptions = {
  title?: string;
  message: string;
  details?: string;
};

export type ShowConfirmOptions = {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  tone?: "danger" | "normal";
};

type GlobalModalsContextValue = {
  showError: (opts: ShowErrorOptions) => void;
  showConfirm: (opts: ShowConfirmOptions) => Promise<boolean>;
};

const GlobalModalsContext = createContext<GlobalModalsContextValue | null>(null);

export function GlobalModalsProvider({ children }: { children: React.ReactNode }) {
  const [errorState, setErrorState] = useState<ShowErrorOptions | null>(null);
  const [confirmState, setConfirmState] = useState<{
    opts: ShowConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const showError = useCallback((opts: ShowErrorOptions) => {
    setErrorState({
      title: opts.title ?? "Error",
      message: opts.message,
      details: opts.details,
    });
  }, []);

  const showConfirm = useCallback((opts: ShowConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({
        opts: {
          title: opts.title,
          message: opts.message,
          confirmText: opts.confirmText ?? "Confirm",
          cancelText: opts.cancelText ?? "Cancel",
          tone: opts.tone ?? "normal",
        },
        resolve,
      });
    });
  }, []);

  const handleConfirmOk = useCallback(() => {
    confirmState?.resolve(true);
    setConfirmState(null);
  }, [confirmState]);

  const handleConfirmCancel = useCallback(() => {
    confirmState?.resolve(false);
    setConfirmState(null);
  }, [confirmState]);

  return (
    <GlobalModalsContext.Provider value={{ showError, showConfirm }}>
      {children}
      <ErrorDialog
        open={errorState != null}
        title={errorState?.title ?? "Error"}
        message={errorState?.message ?? ""}
        details={errorState?.details}
        onClose={() => setErrorState(null)}
      />
      <ConfirmDialog
        open={confirmState != null}
        title={confirmState?.opts.title ?? "Confirm"}
        description={confirmState?.opts.message}
        confirmText={confirmState?.opts.confirmText ?? "Confirm"}
        cancelText={confirmState?.opts.cancelText ?? "Cancel"}
        severity={confirmState?.opts.tone === "danger" ? "destructive" : "normal"}
        loading={false}
        onConfirm={handleConfirmOk}
        onClose={handleConfirmCancel}
      />
    </GlobalModalsContext.Provider>
  );
}

export function useGlobalModals(): GlobalModalsContextValue {
  const ctx = useContext(GlobalModalsContext);
  if (!ctx) {
    throw new Error("useGlobalModals must be used within GlobalModalsProvider");
  }
  return ctx;
}
